<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import {
  HomeIcon,
  InformationCircleIcon,
  UsersIcon,
  FolderOpenIcon,
  NewspaperIcon,
  PhotoIcon,
  EnvelopeIcon,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";
import ApplicationLogo from "@/Components/ApplicationLogo.vue";
import { Link, usePage } from "@inertiajs/vue3";
import { route } from "../../../vendor/tightenco/ziggy/src/js";
import { tWithFallback as twf } from "@/i18n/helper";
import LanguageSwitcher from "@/Components/LanguageSwitcher.vue";

defineProps({
  menu: {
    type: Array,
    default: () => [],
  },
});

const page = usePage();
const tWithFallback = twf;

const stickyMenu = ref(false);
const isMobileMenuVisible = ref(false);

const toggleMobileMenu = () => {
  isMobileMenuVisible.value = !isMobileMenuVisible.value;
};
const closeMobileMenu = () => {
  isMobileMenuVisible.value = false;
};

// Item menu (datar, ikon di atas label). Label tetap lewat i18n.
const navItems = [
  { key: "menu.home", fallback: "Beranda", route: "home", icon: HomeIcon },
  { key: "menu.profile", fallback: "Profil", route: "profil", icon: InformationCircleIcon },
  { key: "menu.leaders", fallback: "Pimpinan", route: "pejabat", icon: UsersIcon },
  { key: "menu.documents", fallback: "Dokumen", route: "dokumen", icon: FolderOpenIcon },
  { key: "menu.information", fallback: "Informasi", route: "informasi", icon: NewspaperIcon },
  { key: "menu.gallery", fallback: "Galeri", route: "galeri", icon: PhotoIcon },
];

const isActive = (name) => {
  try {
    return route().current(name);
  } catch (e) {
    return false;
  }
};

const handleScroll = () => {
  stickyMenu.value = window.scrollY > 0;
};
const handleClickOutside = (event) => {
  if (
    !event.target.closest(".mobile-menu") &&
    !event.target.closest(".toggle-button")
  ) {
    closeMobileMenu();
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("scroll", handleScroll);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("scroll", handleScroll);
});
</script>

<template>
  <div>
    <header class="fixed top-0 left-0 w-full z-[99999]">
      <!-- ===== Top bar ===== -->
      <div class="text-white bg-primary">
        <div
          class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0 h-9 flex items-center justify-between text-xs"
        >
          <div class="flex items-center gap-4">
            <a
              v-if="page.props.apps?.linkemail"
              :href="`mailto:${page.props.apps.linkemail}`"
              class="inline-flex items-center gap-1.5 transition-opacity hover:opacity-80"
            >
              <EnvelopeIcon class="w-3.5 h-3.5" />
              <span class="hidden sm:inline">{{ page.props.apps.linkemail }}</span>
            </a>
          </div>

          <div class="flex items-center gap-3">
            <div class="items-center hidden gap-3 sm:flex">
              <a
                v-if="page.props.apps?.linkfb"
                :href="page.props.apps.linkfb"
                target="_blank"
                rel="noopener noreferrer"
                class="transition-opacity hover:opacity-80"
                title="Facebook"
                ><i class="fa-brands fa-facebook-f"></i
              ></a>
              <a
                v-if="page.props.apps?.linkinstagram"
                :href="page.props.apps.linkinstagram"
                target="_blank"
                rel="noopener noreferrer"
                class="transition-opacity hover:opacity-80"
                title="Instagram"
                ><i class="fa-brands fa-instagram"></i
              ></a>
              <a
                v-if="page.props.apps?.linkyoutube"
                :href="page.props.apps.linkyoutube"
                target="_blank"
                rel="noopener noreferrer"
                class="transition-opacity hover:opacity-80"
                title="YouTube"
                ><i class="fa-brands fa-youtube"></i
              ></a>
              <span class="w-px h-4 bg-white/30"></span>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      <!-- ===== Main bar ===== -->
      <div
        class="bg-white border-b border-gray-200 transition-shadow duration-300"
        :class="{ 'shadow-sm': stickyMenu }"
      >
        <div
          class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0 h-16 grid grid-cols-[1fr_auto_1fr] items-center gap-4"
        >
          <!-- Logo (kiri) -->
          <Link :href="route('home')" class="flex items-center gap-3 w-fit">
            <ApplicationLogo class="w-10 h-10 text-primary" />
            <span
              class="text-sm font-bold sm:text-base text-maroon-800 font-roboto-slab whitespace-nowrap"
            >
              {{ page.props.apps?.namaapps }}
            </span>
          </Link>

          <!-- Desktop menu: di tengah, gaya pill modern -->
          <nav class="items-center hidden gap-1 lg:flex justify-self-center">
            <Link
              v-for="item in navItems"
              :key="item.route"
              :href="route(item.route)"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-full font-roboto-slab"
              :class="
                isActive(item.route)
                  ? 'bg-primary/10 text-primary'
                  : 'text-gray-600 hover:text-primary hover:bg-primary/5'
              "
            >
              <component :is="item.icon" class="w-4 h-4" />
              <span class="whitespace-nowrap">
                {{ tWithFallback(item.key, item.fallback) }}
              </span>
            </Link>
          </nav>

          <!-- Aksi kanan / hamburger -->
          <div class="flex items-center justify-end">
            <button
              aria-label="Buka menu"
              class="block lg:hidden toggle-button text-gray-700"
              @click="toggleMobileMenu"
            >
              <Bars3Icon class="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- ===== Mobile drawer ===== -->
    <Teleport to="body">
      <div
        v-if="isMobileMenuVisible"
        class="mobile-menu fixed inset-0 bg-black bg-opacity-40 z-[100000] lg:hidden"
        @click.self="closeMobileMenu"
      >
        <div
          class="relative w-4/5 h-full max-w-xs p-6 overflow-y-auto bg-white shadow-lg"
        >
          <button
            class="absolute text-gray-500 top-4 right-4 hover:text-gray-800"
            @click="closeMobileMenu"
            aria-label="Tutup menu"
          >
            <XMarkIcon class="w-6 h-6" />
          </button>

          <div class="mb-6">
            <LanguageSwitcher />
          </div>

          <nav>
            <ul class="flex flex-col gap-1">
              <li v-for="item in navItems" :key="item.route">
                <Link
                  :href="route(item.route)"
                  @click="closeMobileMenu"
                  class="flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors font-roboto-slab"
                  :class="
                    isActive(item.route)
                      ? 'text-primary bg-primary/10'
                      : 'text-gray-700 hover:bg-gray-100'
                  "
                >
                  <component :is="item.icon" class="w-5 h-5 text-primary" />
                  {{ tWithFallback(item.key, item.fallback) }}
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </Teleport>
  </div>
</template>
